import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Convert base64 URL-safe VAPID key → Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * PushSubscribeButton — asks for notification permission, subscribes to push,
 * and posts the subscription to the backend. If already subscribed, offers unsubscribe.
 */
export default function PushSubscribeButton() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setSupported(false);
      return;
    }
    setPermission(Notification.permission);
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (_) {}
    })();
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Bildirim izni verilmedi");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      setSubscribed(true);
      toast.success("Bildirimler açıldı");
    } catch (err) {
      toast.error(formatApiError(err) || err?.message || "Abone olunamadı");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Bildirimler kapatıldı");
    } catch (err) {
      toast.error(err?.message || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const testNotify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) {
        toast.success(`Test bildirimi gönderildi (${data.sent}/${data.total})`);
      } else {
        toast.warning(`Bildirim gönderilemedi (${data.total} abone taranmış, hepsi başarısız/gone)`);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Test bildirimi gönderilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-1">
      {!subscribed ? (
        <Button
          data-testid="push-subscribe-btn"
          onClick={subscribe}
          disabled={busy}
          variant="outline"
          size="sm"
          className="border-slate-300 dark:border-slate-700 bg-white dark:bg-transparent text-slate-800 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-700 dark:hover:text-blue-400 rounded-sm text-xs h-8"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Bell className="w-3.5 h-3.5 mr-1.5" /> Bildirimleri Aç</>}
        </Button>
      ) : (
        <>
          <Button
            data-testid="push-test-btn"
            onClick={testNotify}
            disabled={busy}
            variant="outline"
            size="sm"
            className="border-emerald-300 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 hover:text-emerald-900 dark:hover:text-emerald-200 disabled:opacity-60 rounded-sm text-xs h-8"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><BellRing className="w-3.5 h-3.5 mr-1.5" /> Test Bildir</>}
          </Button>
          <Button
            data-testid="push-unsubscribe-btn"
            onClick={unsubscribe}
            disabled={busy}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm h-8 px-2"
            title="Bildirimleri kapat"
          >
            <BellOff className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}
